import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@/components/ui/Spinner';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useCounterpartySearch } from '@/hooks/counterparty';
// The keyboard half of a hand-rolled WAI-ARIA tablist (LR-587): `role="tab"` +
// `aria-selected` alone leaves every tab in the tab order with no way to move
// between them. Imported across features rather than re-written here - the
// contract is generic, and a second copy is the one that silently drifts.
import { useRovingTabs } from '@/features/collateral/components/use-roving-tabs';
import { billingService } from '@/services/billing/billing-service';
import { billingKeys } from '@/services/query-keys/billing-keys';
import { useBillingPreview } from '../hooks/use-billing-preview';
import { useCashPoolPreview } from '../hooks/use-cash-pool-preview';
import { useDownloadPreview } from '../hooks/use-download-preview';
import { CashPoolDocumentView } from './CashPoolDocumentView';
import { StatementDocumentView } from './StatementDocumentView';
import { DownloadMenu } from './DownloadMenu';
import { isCashPoolDocument } from '@/types';
import type {
  BillingStatementType,
  CurrencyBlock,
  PreviewPositionGroup,
  PreviewSection,
  StatementExportFormat,
} from '@/types';

interface Props {
  onClose: () => void;
  /**
   * The page's current filter, carried in as the modal's STARTING point
   * (LR-624 follow-up). The customer's own report came from
   * `/billing?type=cash_pool`: filtering the list to Cash Pool Interest and then
   * opening this modal onto the Trade billing tab lands them on exactly the
   * screen they complained was empty. Starting values only - the modal owns the
   * choice from then on, so switching tab or counterparty inside it does not
   * write back to the URL.
   */
  initialDocType?: BillingStatementType;
  initialCounterparty?: { id: number; name: string };
}

// The preview serves every format the persisted statement does (CSV, PDF - see
// DOWNLOAD_FORMATS for why there is no xlsx). Each one is watermarked by the
// backend as a month-to-date draft, so a preview PDF can't be mistaken for an
// issued invoice.
const PREVIEW_FORMATS: { format: StatementExportFormat; label: string }[] = [
  { format: 'csv', label: 'CSV' },
  { format: 'pdf', label: 'PDF' },
];

// The two documents a counterparty's month can form (LR-624): the trade
// statement and the cash pool interest statement. Same values the list rows
// carry, so the switch and the backend speak one vocabulary.
const DOC_TYPES: { value: BillingStatementType; label: string }[] = [
  { value: 'trade_billing', label: 'Trade billing' },
  { value: 'cash_pool_interest', label: 'Cash pool interest' },
];
const DOC_TYPE_KEYS = DOC_TYPES.map((t) => t.value);
// One panel for both tabs - what changes is the document inside it, not the
// region. Ids are static (one modal at a time), so no generated suffix.
const DOC_PANEL_ID = 'mtd-preview-panel';
const docTabId = (value: BillingStatementType) => `mtd-preview-tab-${value}`;

/**
 * The header's download menu, together with the export it runs (LR-728).
 *
 * Its own component so that the export's once-a-second progress re-renders the
 * menu, not the document beside it: on a counterparty with tens of thousands of
 * positions one render of that document takes longer than the poll interval,
 * and the page would never catch up. Mounted for as long as the modal is -
 * `visible` only hides it - because switching counterparty or tab must not drop
 * an export that is still running.
 */
const PreviewDownload: React.FC<{
  counterparty: number;
  docType: BillingStatementType;
  visible: boolean;
}> = ({ counterparty, docType, visible }) => {
  const { download, isDownloading, exportJob, exportTarget } =
    useDownloadPreview();
  // A running export stays on screen even when the document now on screen has
  // nothing to download (another counterparty, or one still loading): hiding
  // the trigger would take the progress bar and the live region with it, and
  // the trader would conclude the export died.
  if (!visible && !isDownloading) return null;
  // One document per counterparty means one download per format - no narrowing,
  // and nothing for the trader to pick between. That removes a whole class of
  // mistake the per-statement menu had: several books that differed only by
  // account produced menu items nobody could tell apart. The active tab's kind
  // rides along (LR-624), so the file downloaded is the document on screen.
  const options = PREVIEW_FORMATS.map(({ format, label }) => ({
    format,
    label,
    onSelect: () => download({ counterparty, statement_type: docType }, format),
  }));
  // The picker and the tabs stay live during an export, so the running one may
  // be of a document no longer on screen.
  const exportingElsewhere =
    exportTarget !== null &&
    (exportTarget.counterparty !== counterparty ||
      exportTarget.statement_type !== docType);
  return (
    <DownloadMenu
      options={options}
      isDownloading={isDownloading}
      exportJob={exportJob}
      busyElsewhere={exportingElsewhere}
      ariaLabel="Download preview"
    />
  );
};

/**
 * Month-to-date preview modal (LR-508 / T5, reshaped by LR-553): the
 * counterparty's current-month document, view-only, with downloads in the header.
 * Rendered by the same StatementDocumentView as the document page (LR-557 AC),
 * so the two never drift. Currencies are blocks, each closing on its own total;
 * there is deliberately no total across them. Nothing is persisted - this is a
 * live read of unbilled accruals.
 *
 * The modal owns the counterparty choice (LR-557 polish, mirrors
 * GenerateStatementModal): the header select it replaced sat on the page with no
 * visible link to the button it armed. Switching the pick swaps the preview in
 * place - that is now the point; the old page-level open-time snapshot guarded
 * against exactly this only because the choice lived outside the modal.
 */
export const BillingPreviewModal: React.FC<Props> = ({
  onClose,
  initialDocType,
  initialCounterparty,
}) => {
  const [counterparty, setCounterparty] = useState(
    initialCounterparty ? String(initialCounterparty.id) : ''
  );
  // The picked label is kept here rather than looked up: the loaded window moves
  // with the query, so the selection would otherwise blank out on the next
  // keystroke (LR-572). Same reason the page hands its name in rather than an id
  // alone - the filtered counterparty is generally not in the loaded window.
  const [counterpartyName, setCounterpartyName] = useState(
    initialCounterparty?.name ?? ''
  );
  const [cpQuery, setCpQuery] = useState('');
  // Which of the month's two forming documents is shown (LR-624). Only the
  // active one queries: the other's hook is disabled, not just hidden.
  const [docType, setDocType] = useState<BillingStatementType>(
    initialDocType ?? 'trade_billing'
  );
  const { refs: tabRefs, onKeyDown: onTabKeyDown } = useRovingTabs(
    DOC_TYPE_KEYS,
    setDocType
  );
  const dialogRef = useRef<HTMLDivElement>(null);
  // The document scrolls in the modal body; `null` until that element mounts.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  // The row under the header the trade document puts its search field in.
  const [searchSlot, setSearchSlot] = useState<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  // Same query as GenerateStatementModal (shared cache entry), now searched and
  // paged server-side instead of pulled whole (LR-572). Still active
  // counterparties only: a month-to-date read of a deactivated one has no
  // product meaning; its statements live in the list below.
  const {
    options,
    isLoading: cpLoading,
    isError: cpError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCounterpartySearch(cpQuery, true);
  const { data, isLoading, error, dataUpdatedAt } = useBillingPreview(
    counterparty && docType === 'trade_billing'
      ? { counterparty: Number(counterparty) }
      : null
  );
  const {
    data: rawPoolData,
    isLoading: poolLoading,
    error: poolError,
  } = useCashPoolPreview(
    counterparty && docType === 'cash_pool_interest'
      ? { counterparty: Number(counterparty) }
      : null
  );
  // `statement_type` is a QUERY PARAM on an endpoint that already exists, and
  // DRF ignores unknown query params - so a backend pod that predates LR-624
  // answers this call 200 with the TRADE document. Cast straight into
  // `CashPoolDocumentView`, that body reaches `block.sections[].rows` (trade
  // sections carry `groups`) and throws, and /billing has no ErrorBoundary, so
  // the whole page goes white. Same guard the document page and the expanded
  // list row already apply to this document, and for exactly this reason.
  const poolData = isCashPoolDocument(rawPoolData) ? rawPoolData : undefined;
  const poolShapeError = rawPoolData !== undefined && poolData === undefined;

  // Focus the dialog on mount so Escape works without tabbing into it first.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // The document arrives footed but without daily lines (LR-571) - a large
  // counterparty's month has hundreds of thousands of them. A group fetches its
  // own when opened, scoped exactly like the document it sits in.
  //
  // Through the query cache, not a bare await: the endpoint answers with EVERY
  // section of the position, so a fee-mode FOP security - footed once under Fee
  // Income and once under Cash Rebate - would otherwise fire two identical
  // requests and each would discard the half the other wanted.
  //
  // Keyed by the document's fetch as well: a refetch re-foots the groups, and
  // lines cached - or still in flight - for the previous document must not
  // answer a group opened under the new one.
  const loadRows = useCallback(
    (block: CurrencyBlock) =>
      async (section: PreviewSection, group: PreviewPositionGroup) => {
        const rows = await queryClient.fetchQuery({
          queryKey: billingKeys.positionRows(
            `preview:${counterparty}:${block.account}:${block.currency}:${dataUpdatedAt}`,
            group.position_id
          ),
          queryFn: () =>
            billingService.getPreviewRows({
              counterparty: Number(counterparty),
              account: block.account,
              currency: block.currency,
              position: group.position_id,
            }),
        });
        // Only the opened section's block: a fee-mode FOP position books a fee leg
        // and a rebate leg, which the backend foots under two different headings.
        // Pouring both under one of them would contradict the very subtotal and
        // line count the group is showing.
        return rows.sections.find((s) => s.key === section.key) ?? null;
      },
    [counterparty, dataUpdatedAt, queryClient]
  );

  // Names the document kind as well as the counterparty: the two tabs download
  // two different files, and a screenshot of the modal has to say which one.
  const kind =
    docType === 'cash_pool_interest' ? 'Cash Pool Interest' : 'Trade Billing';
  const title = counterpartyName
    ? `Month-to-date Preview: ${kind} — ${counterpartyName}`
    : `Month-to-date Preview: ${kind}`;

  const hasDownloadable =
    docType === 'cash_pool_interest'
      ? Boolean(
          poolData &&
          (poolData.blocks.length > 0 || (poolData.postings?.length ?? 0) > 0)
        )
      : Boolean(data && data.blocks.length > 0);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        // No overflow-hidden: SearchableSelect / DownloadMenu drop their lists as
        // absolute children, and a clipping dialog cuts them at its edge. The
        // body clips itself; its bottom corners are rounded to match the shell.
        className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-[10px] bg-lr-surface shadow-xl focus:outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-preview-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      >
        <div className="flex items-center justify-between gap-4 border-b border-lr-border px-6 py-4">
          <h2
            id="billing-preview-title"
            className="shrink-0 text-[15px] font-semibold text-lr-fg"
          >
            {title}
          </h2>
          <div className="flex min-w-0 items-center gap-2">
            <SearchableSelect
              value={counterparty}
              onChange={(cp, option) => {
                setCounterparty(cp);
                setCounterpartyName(option?.label ?? '');
              }}
              options={options}
              selectedLabel={counterpartyName || undefined}
              placeholder="Select counterparty"
              searchPlaceholder="Search counterparties…"
              // Not disabled while loading: the dropdown says "Loading…" itself,
              // and locking the control would block typing into the search that
              // resolves it.
              className="w-64"
              onSearchChange={setCpQuery}
              isLoading={cpLoading}
              isError={cpError}
              hasMore={hasNextPage}
              // `cancelRefetch: false` so a burst of scroll events joins the page
              // already in flight instead of cancelling and re-issuing it.
              onLoadMore={() => fetchNextPage({ cancelRefetch: false })}
              isLoadingMore={isFetchingNextPage}
              buttonProps={{
                'aria-label': 'Preview counterparty',
                'data-testid': 'mtd-preview-cp',
              }}
            />
            <PreviewDownload
              counterparty={Number(counterparty)}
              docType={docType}
              visible={hasDownloadable}
            />
            <button
              onClick={onClose}
              className="text-2xl leading-none text-lr-muted hover:text-lr-fg"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* A row of its own: beside the title and the picker it pushes the
            download out of the modal on a long counterparty name. Hidden while
            no trade document fills it. */}
        <div
          ref={setSearchSlot}
          className="flex justify-end border-b border-lr-border px-6 py-2 empty:hidden"
        />

        {/* The statement itself is a document: a white sheet on the canvas tint,
            matching how it prints. The modal chrome above stays flat. */}
        <div
          ref={setScroller}
          className="space-y-6 overflow-auto rounded-b-[10px] bg-lr-bg p-6"
        >
          {/* Which of the month's two forming documents to show (LR-624). Local
              to the modal (not a routed tabset) and exactly two values - but a
              real tablist all the same, so it carries the roving tabindex and
              arrow/Home/End keys the role promises. */}
          <div
            role="tablist"
            aria-label="Preview document type"
            className="inline-flex rounded-[8px] border border-lr-border bg-lr-surface p-0.5"
          >
            {DOC_TYPES.map(({ value, label }, index) => (
              <button
                key={value}
                type="button"
                role="tab"
                id={docTabId(value)}
                aria-controls={DOC_PANEL_ID}
                aria-selected={docType === value}
                tabIndex={docType === value ? 0 : -1}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                onClick={() => setDocType(value)}
                className={`rounded-[6px] px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
                  docType === value
                    ? 'bg-lr-accent-weak text-lr-accent'
                    : 'text-lr-muted hover:text-lr-fg'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id={DOC_PANEL_ID}
            aria-labelledby={docTabId(docType)}
            // The cash pool document is static tables and prose - no focusable
            // descendant - so under the APG the panel itself must be a tab stop,
            // otherwise Tab jumps from the strip straight past the whole
            // document to Close and a keyboard user cannot scroll it.
            tabIndex={0}
            className="space-y-6 focus:outline-none focus-visible:ring-1 focus-visible:ring-lr-accent"
          >
            {!counterparty && (
              <div className="rounded-[10px] border border-dashed border-lr-border-2 bg-lr-surface py-12 text-center text-[12.5px] text-lr-faint">
                Select a counterparty to see its forming month-to-date document.
              </div>
            )}
            {/* Gated on the ACTIVE tab, not OR-ed across both: the inactive
                hook only reads clean because it re-keys when disabled, which is
                an invariant of the hooks, not of this component. */}
            {(docType === 'trade_billing' ? isLoading : poolLoading) && (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            )}
            {(docType === 'trade_billing'
              ? Boolean(error)
              : Boolean(poolError) || poolShapeError) && (
              <div className="text-lr-neg">
                Failed to load month-to-date preview.
              </div>
            )}

            {docType === 'trade_billing' &&
              data &&
              data.blocks.length === 0 && (
                <div className="rounded-[10px] border border-dashed border-lr-border-2 bg-lr-surface py-12 text-center text-[12.5px] text-lr-faint">
                  No billable month-to-date accruals for this counterparty.
                  <span className="mt-1 block text-[11px]">
                    Note: repo (GMRA) trade interest is realised in the
                    Repurchase Price at termination; GMRA cash pool interest is
                    on the Cash pool interest tab.
                  </span>
                </div>
              )}
            {docType === 'trade_billing' && data && data.blocks.length > 0 && (
              <div className="rounded-[10px] border border-lr-border bg-lr-surface px-7 py-6">
                {/* Keyed: another counterparty's document starts on an empty
                    search, as on the page, even when it comes from the cache. */}
                <StatementDocumentView
                  key={counterparty}
                  document={data}
                  loadRows={loadRows}
                  scrollElement={scroller}
                  searchSlot={searchSlot}
                />
              </div>
            )}

            {docType === 'cash_pool_interest' &&
              poolData &&
              poolData.blocks.length === 0 &&
              (poolData.postings?.length ?? 0) === 0 && (
                <div className="rounded-[10px] border border-dashed border-lr-border-2 bg-lr-surface py-12 text-center text-[12.5px] text-lr-faint">
                  No cash pool interest accrued this month for this
                  counterparty.
                  {/* The date is backend `GMSLA_POOL_INTEREST_FROM` (billing/services/accrual.py). */}
                  <span className="mt-1 block text-[11px]">
                    Interest posts nightly on funded pools; a GMRA pool is
                    funded by a margin call posting and a GMSLA pool by its
                    trade bookings, accruing from 9 Nov 2026 once the
                    trade&apos;s cash has settled.
                  </span>
                </div>
              )}
            {docType === 'cash_pool_interest' &&
              poolData &&
              (poolData.blocks.length > 0 ||
                (poolData.postings?.length ?? 0) > 0) && (
                <div className="rounded-[10px] border border-lr-border bg-lr-surface px-7 py-6">
                  <CashPoolDocumentView document={poolData} preview />
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};
